import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import APIService from "../APIService";
import FacultyDashboardShell from "./FacultyDashboardShell";
import { getFacultyWorkspaceCourse, setFacultyWorkspaceCourse } from "../utils/facultyWorkspace";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Dashboard.css";

const EMPTY_COURSE_FORM = {
  courseId: "",
  name: "",
  description: "",
  professorId: "",
};

const EMPTY_OWNER_FORM = {
  professorId: "",
  reason: "",
};

const UNASSIGNED_COURSE_OWNER_ID = "unassigned-course-owner";

function ManagerDashboard() {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [currentUserRole, setCurrentUserRole] = useState("");
  const [currentUserId, setCurrentUserId] = useState("");
  const [courses, setCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState(() => getFacultyWorkspaceCourse()?.courseId || "");
  const [ownershipAudit, setOwnershipAudit] = useState({ assignableUsers: [] });
  const [courseForm, setCourseForm] = useState(EMPTY_COURSE_FORM);
  const [ownerForm, setOwnerForm] = useState(EMPTY_OWNER_FORM);
  const [isCreatingCourse, setIsCreatingCourse] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const isAdminLike = currentUserRole === "admin" || currentUserRole === "staff";
  const selectedCourse = useMemo(
    () => courses.find((course) => course.courseId === selectedCourseId) || null,
    [courses, selectedCourseId]
  );
  const assignableOwners = useMemo(() => ownershipAudit.assignableUsers || [], [ownershipAudit]);

  const loadCourses = useCallback(async (preferredCourseId = null) => {
    try {
      const data = await APIService.GetCourses(true, true);
      const normalized = Array.isArray(data) ? data : [];
      setCourses(normalized);

      const nextCourseId =
        preferredCourseId ||
        (normalized.some((course) => course.courseId === selectedCourseId) ? selectedCourseId : normalized[0]?.courseId) ||
        "";

      setSelectedCourseId(nextCourseId);
      setIsCreatingCourse(!nextCourseId);
    } catch (loadError) {
      setError(loadError.message || "Failed to load courses.");
    }
  }, [selectedCourseId]);

  const loadOwnershipAudit = useCallback(async () => {
    if (!isAdminLike) {
      setOwnershipAudit({ assignableUsers: [] });
      return;
    }

    try {
      const data = await APIService.GetCourseOwnershipAudit();
      setOwnershipAudit({
        assignableUsers: Array.isArray(data?.assignableUsers) ? data.assignableUsers : [],
      });
    } catch (loadError) {
      setError(loadError.message || "Failed to load course ownership options.");
    }
  }, [isAdminLike]);

  useEffect(() => {
    let active = true;

    async function bootstrap() {
      try {
        const data = await APIService.GetUserPrimaryAttribute();
        const isManager = Boolean(data?.faculty_access);

        if (!isManager) {
          navigate("/", { replace: true });
          return;
        }

        if (!active) return;
        setAuthorized(true);
        setCurrentUserRole(data?.app_role || "");
        const currentUser = await APIService.GetCurrentUser().catch(() => null);
        if (currentUser?.unique_id) {
          setCurrentUserId(currentUser.unique_id);
        }
      } catch (_error) {
        navigate("/", { replace: true });
      } finally {
        if (active) {
          setCheckingAuth(false);
        }
      }
    }

    bootstrap();
    return () => {
      active = false;
    };
  }, [navigate]);

  useEffect(() => {
    if (!authorized) {
      return;
    }

    loadCourses().catch(() => {});
  }, [authorized, loadCourses]);

  useEffect(() => {
    if (!authorized) {
      return;
    }

    loadOwnershipAudit().catch(() => {});
  }, [authorized, loadOwnershipAudit]);

  useEffect(() => {
    if (selectedCourse) {
      setCourseForm({
        courseId: selectedCourse.courseId || "",
        name: selectedCourse.name || "",
        description: selectedCourse.description || "",
        professorId: selectedCourse.professorId || "",
      });
      setOwnerForm((current) => ({
        professorId:
          selectedCourse.professorId === UNASSIGNED_COURSE_OWNER_ID
            ? currentUserId
            : (current.professorId || selectedCourse.professorId || currentUserId),
        reason: "",
      }));
      setIsCreatingCourse(false);
      return;
    }

    if (!selectedCourseId) {
      setCourseForm({ ...EMPTY_COURSE_FORM, professorId: currentUserId });
      setOwnerForm({ ...EMPTY_OWNER_FORM, professorId: currentUserId });
      setIsCreatingCourse(true);
    }
  }, [currentUserId, selectedCourse, selectedCourseId]);

  useEffect(() => {
    setFacultyWorkspaceCourse(selectedCourse);
  }, [selectedCourse]);

  function resetFeedback() {
    setMessage("");
    setError("");
  }

  function beginCreateCourse() {
    resetFeedback();
    setSelectedCourseId("");
    setCourseForm({ ...EMPTY_COURSE_FORM, professorId: currentUserId });
    setOwnerForm({ ...EMPTY_OWNER_FORM, professorId: currentUserId });
    setIsCreatingCourse(true);
    setFacultyWorkspaceCourse(null);
  }

  function selectCourse(courseId) {
    resetFeedback();
    setSelectedCourseId(courseId);
    setIsCreatingCourse(false);
  }

  async function handleCourseSubmit(event) {
    event.preventDefault();
    resetFeedback();

    try {
      if (isCreatingCourse) {
        await APIService.CreateCourse(courseForm);
        setMessage("Course created.");
        await loadCourses(courseForm.courseId);
      } else if (selectedCourseId) {
        await APIService.UpdateCourse(selectedCourseId, {
          name: courseForm.name,
          description: courseForm.description,
        });
        setMessage("Course updated.");
        await loadCourses(selectedCourseId);
      }
    } catch (submitError) {
      setError(submitError.message || "Unable to save course.");
    }
  }

  async function handleArchiveCourse() {
    if (!selectedCourseId) return;
    resetFeedback();

    try {
      if (selectedCourse?.isActive === false) {
        await APIService.UpdateCourse(selectedCourseId, { isActive: true });
        setMessage("Course restored.");
      } else {
        await APIService.DeleteCourse(selectedCourseId, false);
        setMessage("Course archived.");
      }
      await loadCourses(selectedCourseId);
    } catch (actionError) {
      setError(actionError.message || "Unable to update course status.");
    }
  }

  async function handleDeleteCourse() {
    if (!selectedCourseId) return;
    resetFeedback();

    try {
      await APIService.DeleteCourse(selectedCourseId, true);
      setMessage("Course deleted.");
      await loadCourses();
    } catch (actionError) {
      setError(actionError.message || "Unable to delete course.");
    }
  }

  async function handleOwnerReassignment(event) {
    event.preventDefault();
    if (!selectedCourseId || !ownerForm.professorId) return;
    resetFeedback();

    try {
      const result = await APIService.ReassignCourseOwner(selectedCourseId, ownerForm);
      setMessage(result.message || "Course owner updated.");
      await Promise.all([loadCourses(selectedCourseId), loadOwnershipAudit()]);
    } catch (submitError) {
      setError(submitError.message || "Unable to reassign course owner.");
    }
  }

  function openContentWorkspace() {
    if (!selectedCourseId) {
      return;
    }
    navigate("/faculty/uploads");
  }

  if (checkingAuth || !authorized) {
    return null;
  }

  return (
    <FacultyDashboardShell
      title="My Courses"
      description="Choose a course or create a new one."
      message={message}
      error={error}
      workspaceCourse={selectedCourse}
      workspaceMode={selectedCourse ? "selected_course" : "all_courses"}
      showWorkspaceBanner={false}
    >
      <main id="main-content" className="faculty-course-page faculty-courses-light">
        <aside className="faculty-course-sidebar" aria-label="My courses">
          <div className="faculty-course-sidebar-header">
            <div>
              <h2>My Courses</h2>
              <p className="faculty-card-copy">Select a course to open its workspace.</p>
            </div>
            <button className="btn btn-create-course" type="button" onClick={beginCreateCourse}>
              New Course
            </button>
          </div>

          <div className="faculty-course-list minimal">
            {courses.map((course) => (
              <button
                key={course.courseId}
                type="button"
                className={`faculty-course-item ${selectedCourseId === course.courseId ? "active" : ""}`}
                onClick={() => selectCourse(course.courseId)}
              >
                <div>
                  <strong>{course.courseId}</strong>
                  <span>{course.name || "Untitled course"}</span>
                  {(course.section || course.semester) && (
                    <span>{[course.section, course.semester].filter(Boolean).join(" · ")}</span>
                  )}
                </div>
                {course.isActive === false && <span className="faculty-status-pill archived">Archived</span>}
              </button>
            ))}
            {courses.length === 0 && <div className="faculty-empty-state">No courses yet.</div>}
          </div>
        </aside>

        <section className="faculty-course-main" aria-label="Course selection and details">
          <div className="faculty-card faculty-course-entry-card">
            <div className="faculty-course-header">
              <div>
                <div className="faculty-eyebrow">{isCreatingCourse ? "Create Course" : "Selected Course"}</div>
                <h1>{isCreatingCourse ? "New Course" : (selectedCourse?.courseId || "Choose a course")}</h1>
                {!isCreatingCourse && selectedCourse && (
                  <p>
                    {selectedCourse.name || "Untitled course"}
                    {(selectedCourse.section || selectedCourse.semester) && ` · ${[selectedCourse.section, selectedCourse.semester].filter(Boolean).join(" · ")}`}
                  </p>
                )}
              </div>

              {!isCreatingCourse && selectedCourse && (
                <div className="faculty-page-actions">
                  <button className="btn btn-create-course" type="button" onClick={openContentWorkspace}>
                    Open Content
                  </button>
                  <button className="btn btn-outline-secondary faculty-secondary-button" type="button" onClick={() => navigate("/faculty/review")}>
                    Sessions
                  </button>
                  <button className="btn btn-outline-secondary faculty-secondary-button" type="button" onClick={() => navigate("/faculty/insights")}>
                    Insights
                  </button>
                </div>
              )}
            </div>

            <form className="faculty-form-grid faculty-form-grid-compact" onSubmit={handleCourseSubmit}>
              <label>
                <span>Course ID</span>
                <input
                  className="form-control"
                  value={courseForm.courseId}
                  disabled={!isCreatingCourse}
                  onChange={(event) => setCourseForm({ ...courseForm, courseId: event.target.value })}
                  required
                />
              </label>
              <label>
                <span>Course Name</span>
                <input
                  className="form-control"
                  value={courseForm.name}
                  onChange={(event) => setCourseForm({ ...courseForm, name: event.target.value })}
                  required
                />
              </label>
              <label className="faculty-form-span">
                <span>Description</span>
                <textarea
                  className="form-control"
                  rows="3"
                  value={courseForm.description}
                  onChange={(event) => setCourseForm({ ...courseForm, description: event.target.value })}
                />
              </label>
              <div className="faculty-inline-actions">
                <button className="btn btn-create-course" type="submit">
                  {isCreatingCourse ? "Create Course" : "Save Details"}
                </button>
                {isCreatingCourse && (
                  <button className="btn btn-outline-secondary faculty-secondary-button" type="button" onClick={() => loadCourses()}>
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>

          {!isCreatingCourse && selectedCourse && (
            <div className="faculty-card faculty-course-secondary-actions">
              <div className="faculty-section-heading">
                <h2>More Actions</h2>
              </div>
              <div className="faculty-inline-actions faculty-inline-actions-wrap">
                <button className="btn btn-outline-secondary faculty-secondary-button faculty-subtle-action" type="button" onClick={handleArchiveCourse}>
                  {selectedCourse.isActive === false ? "Restore Course" : "Archive Course"}
                </button>
                <button className="btn btn-outline-danger btn-sm faculty-subtle-action" type="button" onClick={handleDeleteCourse}>
                  Delete Course
                </button>
              </div>
            </div>
          )}

          {!isCreatingCourse && selectedCourse?.requiresOwnershipReview && isAdminLike && (
            <section className="faculty-card faculty-ownership-card">
              <div className="faculty-section-heading">
                <h2>Owner Review</h2>
                <span className="faculty-status-pill archived">Needs review</span>
              </div>

              <form className="faculty-form-grid faculty-form-grid-compact" onSubmit={handleOwnerReassignment}>
                <label>
                  <span>Assign To</span>
                  <select
                    className="form-select"
                    value={ownerForm.professorId}
                    onChange={(event) => setOwnerForm({ ...ownerForm, professorId: event.target.value })}
                  >
                    <option value="">Select owner</option>
                    {assignableOwners.map((owner) => (
                      <option key={owner.uniqueId} value={owner.uniqueId}>
                        {owner.displayName} ({owner.role})
                      </option>
                    ))}
                  </select>
                </label>
                <label className="faculty-form-span">
                  <span>Note</span>
                  <input
                    className="form-control"
                    value={ownerForm.reason}
                    onChange={(event) => setOwnerForm({ ...ownerForm, reason: event.target.value })}
                    placeholder="Optional"
                  />
                </label>
                <div className="faculty-inline-actions">
                  <button className="btn btn-create-course" type="submit">
                    Save Owner
                  </button>
                </div>
              </form>
            </section>
          )}
        </section>
      </main>
    </FacultyDashboardShell>
  );
}

export default ManagerDashboard;
